import { FormikHelpers } from 'formik';
import {
    FieldValidationData,
    FormikFieldStatusData,
    GeneralException,
    ValidationMessage,
    ValidationProblemDetails,
    ValidationResult,
} from 'types/validation';

export const validationResultUtils = {
    DEFAULT_ISSUE_ID: 'default-ui-validation-issue-id',
    createEmpty: <TFieldName extends string | number | symbol = string>(): ValidationResult<TFieldName> => ({
        globalIssues: [],
        issues: [{ index: 0, id: validationResultUtils.DEFAULT_ISSUE_ID, fields: [], generalValidationMessages: [] }],
    }),

    isEmpty: (result: ValidationResult<any> | null | undefined): boolean => {
        return !result || (result.globalIssues.length === 0 && result.issues.length === 0);
    },

    hasErrors: (result: ValidationResult<any> | null): boolean => {
        if (!result) return false;
        return (
            result.globalIssues.some((issue) => issue.severity === 'Error') ||
            result.issues.some(
                (objResult) =>
                    objResult.generalValidationMessages.some((msg) => msg.severity === 'Error') ||
                    objResult.fields.some((field) => field.validationMessages.some((msg) => msg.severity === 'Error'))
            )
        );
    },

    hasWarnings: (result: ValidationResult | null): boolean => {
        if (!result) return false;
        return (
            result.globalIssues.some((issue) => issue.severity === 'Warning') ||
            result.issues.some(
                (objResult) =>
                    objResult.generalValidationMessages.some((msg) => msg.severity === 'Warning') ||
                    objResult.fields.some((field) => field.validationMessages.some((msg) => msg.severity === 'Warning'))
            )
        );
    },

    // returns global and general validation errors messages
    getAllGlobalErrors: (result: ValidationResult): ValidationMessage[] => {
        const errors: ValidationMessage[] = [];

        errors.push(...result.globalIssues.filter((issue) => issue.severity === 'Error'));

        result.issues.forEach((objResult) => {
            errors.push(...objResult.generalValidationMessages.filter((msg) => msg.severity === 'Error'));
            objResult.fields.forEach((field) => {
                errors.push(...field.validationMessages.filter((msg) => msg.severity === 'Error'));
            });
        });

        return errors;
    },

    // returns global and general validation warnings messages as well as field warnings
    getAllGlobalWarnings: (result: ValidationResult): ValidationMessage[] => {
        const warnings: ValidationMessage[] = [];

        warnings.push(...result.globalIssues.filter((issue) => issue.severity === 'Warning'));

        result.issues.forEach((objResult) => {
            warnings.push(...objResult.generalValidationMessages.filter((msg) => msg.severity === 'Warning'));
            objResult.fields.forEach((field) => {
                warnings.push(...field.validationMessages.filter((msg) => msg.severity === 'Warning'));
            });
        });

        return warnings;
    },

    clearGlobalErrors: (result: ValidationResult): void => {
        result.globalIssues = result.globalIssues.filter((issue) => issue.severity !== 'Error');
        for (const issue of result.issues) {
            issue.generalValidationMessages = issue.generalValidationMessages.filter((msg) => msg.severity !== 'Error');
        }
    },

    getFormikFieldStatusData: (result: ValidationResult): Record<string, FormikFieldStatusData> => {
        const formikFieldStatus: Record<string, FormikFieldStatusData> = {};
        for (const issue of result.issues) {
            for (const field of issue.fields) {
                formikFieldStatus[field.name] = {
                    errors: field.validationMessages
                        .filter((msg) => msg.severity === 'Error')
                        .map((msg) => msg.message),
                    warnings: field.validationMessages
                        .filter((msg) => msg.severity === 'Warning')
                        .map((msg) => msg.message),
                };
            }
        }
        return formikFieldStatus;
    },

    getFieldValidationData: (result: ValidationResult): Record<string, FieldValidationData> => {
        const fieldValidationData: Record<string, FieldValidationData> = {};
        for (const issue of result.issues) {
            for (const field of issue.fields) {
                fieldValidationData[field.name] = { ...field };
            }
        }
        return fieldValidationData;
    },

    setFormikStatusAndErrors: <TFieldName extends string | number | symbol = string>(
        data: ValidationResult<TFieldName>,
        formik: FormikHelpers<any>
    ) => {
        const status: Partial<Record<TFieldName, FormikFieldStatusData>> = {};
        data.issues.forEach((issue) => {
            issue.fields.forEach((field) => {
                status[field.name] = {
                    errors: field.validationMessages
                        .filter((msg) => msg.severity === 'Error')
                        .map((msg) => msg.message),
                    warnings: field.validationMessages
                        .filter((msg) => msg.severity === 'Warning')
                        .map((msg) => msg.message),
                };
            });
        });

        formik.setStatus(status);
    },
};

export function isValidationProblemDetailsResponse(value: unknown): value is ValidationProblemDetails {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const details = value as ValidationProblemDetails;

    if (typeof details.errors === 'object' && Object.keys(details.errors || {}).length > 0) {
        const errorsIsCorrect = Object.values(details.errors).every(
            (error) => Array.isArray(error) && error.every((msg) => typeof msg === 'string')
        );
        if (!errorsIsCorrect) {
            return false;
        }
    }

    return typeof details.type === 'string' && typeof details.title === 'string' && typeof details.status === 'number';
}

export function isGeneralException(value: unknown): value is GeneralException {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const exception = value as GeneralException;

    return (
        typeof exception.code === 'number' &&
        typeof exception.error === 'string' &&
        (exception.details === undefined || typeof exception.details === 'string') &&
        (exception.errorMessageType === undefined || typeof exception.errorMessageType === 'number') &&
        (exception.errorType === undefined || typeof exception.errorType === 'string') &&
        (exception.instance === undefined || typeof exception.instance === 'string')
    );
}
